import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Popover, { PopoverPosition } from "@mui/material/Popover";
import { styled } from "@mui/material/styles";
import Button from "designSystem/components/button/Button";
import Text from "designSystem/components/text/Text";
import TextField from "designSystem/components/textfield/TextField";
import { useLocale } from "designSystem/locale";
import { POPOVER_BOTTOM } from "designSystem/styles/popover-positions";
import { STANDARD_BORDER_STYLE } from "designSystem/styles/style-functions";
import { ButtonEnum } from "designSystem/types/button-types";
import React from "react";

const PREFIX = "LinkPopup";

const classes = {
	root: `${PREFIX}-root`,
	title: `${PREFIX}-title`,
	buttons: `${PREFIX}-buttons`,
	buttonSpacer: `${PREFIX}-buttonSpacer`,
};

const StyledPopover = styled(Popover)(({
	theme,
}) => ({
	[`& .${classes.root}`]: {
		background: "white",
		border: STANDARD_BORDER_STYLE,
		borderRadius: "3px",
		fontSize: "12px",
		padding: theme.spacing(3),
		width: "210px",
	},
	[`& .${classes.title}`]: {
		display: "block",
		marginBottom: theme.spacing(1),
		textAlign: "left",
	},
	[`& .${classes.buttons}`]: {
		flexWrap: "nowrap",
		justifyContent: "flex-end",
		marginTop: theme.spacing(3),
	},
	[`& .${classes.buttonSpacer}`]: {
		width: theme.spacing(2),
	},
}));

export interface LinkPopupProps {
	anchor: HTMLElement;
	url: string;
	onOK: (url: string) => void;
	onCancel: () => void;
}

const LinkPopup: React.FunctionComponent<LinkPopupProps> = ({ anchor, url, onOK, onCancel }) => {
	const locale = useLocale();
	const [inputUrl, setInputUrl] = React.useState<string>(url);
	const [anchorPosition, setAnchorPosition] = React.useState<PopoverPosition>({ left: 0, top: 0 });

	// We should capture rect as soon as possible, component may not be mounted during useEffect call 
	const lastAnchorRect = anchor?.getBoundingClientRect();
	React.useEffect(
		() => {
			// Because of React rerender, the anchor component is not always mount and is part of the dom
			// So we should use last Captured rect, only when it is valid
			// When popup added to the DOM, the first render call we get mounted anchor, 
			// but during second render call anchor Element has empty rect
			if (lastAnchorRect?.width > 0) {
				setAnchorPosition({
					left: (lastAnchorRect.left + lastAnchorRect.right) / 2,
					top: lastAnchorRect.top - 4,
				});
			}
			return () => null;
		},
		[anchor],
	);

	return (
		<StyledPopover
			anchorPosition={anchorPosition}
			anchorReference="anchorPosition"
			onClose={onCancel}
			open
			{...POPOVER_BOTTOM}
		>
			<Box className={classes.root}>
				<Text
					bold
					className={classes.title}
					text={locale.resource("Link_Browse")}
				/>
				<TextField
					autoFocus
					fullWidth
					onChange={(e) => setInputUrl(e.target.value)}
					onEnter={() => onOK(inputUrl)}
					value={inputUrl}
				/>
				<Grid
					className={classes.buttons}
					container
					direction="row"
				>
					<Button
						design="primary"
						onClick={() => onOK(inputUrl)}
						res={ButtonEnum.BUTTON_OK}
					/>
					<Box className={classes.buttonSpacer} />
					<Button
						design="link"
						onClick={() => onCancel()}
						res={ButtonEnum.BUTTON_CANCEL}
					/>
				</Grid>
			</Box>
		</StyledPopover>
	);
};

export default LinkPopup;
